export type MergableObject = Record<string, unknown>;

/**
 * Utility function that merges two obejct together, resulting in an object
 * that has all the keys from both objects. If a key exists in both objects, the value
 * depends on the type of the value in the first object:
 * - If the value is an object, the value from the second object is merged into it using `mergeObject`
 * - If value is an Array, the value from the second object is merged into it value from the first object
 * - If the value is not an object or array, the value from the second object is used (overwriting the value from the first object, if override is true)
 *
 * @param objectA Object indexable by string
 * @param objectB Object indexable by string
 * @param overwriteSimpleTypes If true, the value from objectB will be used if the key exists in both objects
 * @returns Merged object
 */
// eslint-disable-next-line complexity
export function mergeObjects(
  objectA: MergableObject,
  objectB: MergableObject,
  overwriteSimpleTypes = false,
): MergableObject {
  // Validation
  const incorrectA = !objectA || typeof objectA !== "object";
  const incorrectB = !objectB || typeof objectB !== "object";
  if (incorrectA && incorrectB) {
    // Both objects are null or undefined, return an empty object
    return {};
  }
  if (incorrectA) {
    // If objectA is null or undefined, return objectB
    return objectB;
  }
  if (incorrectB) {
    // If objectB is null or undefined, return objectA
    return objectA;
  }

  // Clone objectA to avoid mutating it
  const mergedObject: MergableObject = { ...objectA };

  for (const key in objectB) {
    const valueA = objectA[key];
    const valueB = objectB[key];

    if (valueA === undefined) {
      mergedObject[key] = valueB;
    } else if (Array.isArray(valueA)) {
      mergedObject[key] = [
        ...valueA,
        ...(Array.isArray(valueB) ? valueB : [valueB]),
      ];
    } else if (typeof valueA === "object" && typeof valueB === "object") {
      mergedObject[key] = mergeObjects(
        valueA as MergableObject,
        valueB as MergableObject,
        overwriteSimpleTypes,
      );
    } else if (overwriteSimpleTypes) {
      mergedObject[key] = valueB;
    }
  }

  return mergedObject;
}
